import type { VoidComponent } from "solid-js";
import { createMemo, getOwner, runWithOwner, Show } from "solid-js";
import type { ExtendableInputProps } from "../components/inputs/Input";
import Input from "../components/inputs/Input";
import type SubformId from "../state/SubformId";
import SubformService from "../services/SubformService";
import { TextAndIconButton } from "../components/button/Button";
import { useTranslation } from "../state/i18n";
import SubformShowEvent from "../events/SubformShowEvent";
import { useFormServices } from "../services/formServices";
import type { SelectOption } from "../types/Menu";
import { SingleSelect } from "../components/menu/Select";
import { isNil } from "lodash-es";
import { useModalManager } from "../components/modal/ModalManager";
import SubformCreateModal from "../pages/formEditor/subforms/SubformCreateModal";

type SubformProps = {
  value?: SubformId | null;
  onChange: (subformId: SubformId | null) => void;
} & ExtendableInputProps;

const SubformInput: VoidComponent<SubformProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const { currentScope } = useFormServices();
  const owner = getOwner();

  const options = createMemo(() =>
    SubformService.getAllowedSubforms(currentScope).map(
      (subform) =>
        ({
          variant: "icon",
          value: subform.uid,
          label: subform.meta.name,
          icon: { name: "documentDuplicate", type: "solid", class: "icon-primary" },
        }) satisfies SelectOption<SubformId>,
    ),
  );

  const isRequired = createMemo(() => props.required ?? false);
  const value = createMemo(() =>
    !isNil(props.value) && !isNil(SubformService.getSubform(props.value)) ? props.value : null,
  );

  return (
    <Input required={isRequired()} errorMessage={props.errorMessage} info={props.info} label={props.label}>
      {() => (
        <div class="flex gap-1">
          <div class="min-w-0 grow">
            <SingleSelect
              hasError={!isNil(props.errorMessage)}
              options={options()}
              onSelect={(value) => props.onChange(value)}
              onClear={() => props.onChange(null)}
              ariaLabel={props.label}
              value={value()}
              placeholder={t(($) => $.FIELD_PROPERTY_SUBFORM_TARGET_FORM_ID_PLACEHOLDER)}
              sameWidth
            />
          </div>
          <Show
            when={value()}
            fallback={
              <div class="ml-1 flex items-center gap-x-2">
                <span class="text-center text-header-sm text-secondary uppercase">
                  {t(($) => $.FIELD_PROPERTY_SUBFORM_TARGET_FORM_ID_OR)}
                </span>
                <TextAndIconButton
                  text={t(($) => $.FIELD_PROPERTY_SUBFORM_TARGET_FORM_ID_CREATE)}
                  icon="plus"
                  variant="light"
                  onClick={() => {
                    open({
                      skipClosingAnimation: true,
                      content: () => (
                        <SubformCreateModal
                          onCreate={(subform) => {
                            props.onChange(subform.uid);
                            new SubformShowEvent(subform.uid).send();
                          }}
                        />
                      ),
                    });
                  }}
                />
              </div>
            }
          >
            {(subformId) => (
              <TextAndIconButton
                text={t(($) => $.FIELD_PROPERTY_SUBFORM_TARGET_FORM_ID_EDIT)}
                icon="pencil"
                variant="secondary"
                onClick={() => {
                  runWithOwner(owner, () => new SubformShowEvent(subformId()).send());
                }}
              />
            )}
          </Show>
        </div>
      )}
    </Input>
  );
};

export default SubformInput;
